import { type QueryKey, skipToken, useInfiniteQuery } from '@tanstack/react-query';
import isObject from 'lodash/isObject';

import { Skills } from '@air/api';
import { FIVE_MINUTES } from '@air/utilities';
import { callAirApi } from '@air/utils-api';

import {
  toWorkspaceAgentSkillsListApiParams,
  type UseWorkspaceAgentSkillsListParams,
} from '~/hooks/queries/workspace-agent-skills/workspaceAgentSkillsListQueryParams';

export type { Skill } from '~/hooks/queries/skills/usePrivateSkillsList';
export type { UseWorkspaceAgentSkillsListParams } from '~/hooks/queries/workspace-agent-skills/workspaceAgentSkillsListQueryParams';

export const WORKSPACE_AGENT_SKILLS_LIST_KEY = 'WORKSPACE_AGENT_SKILLS_LIST';

export type WorkspaceAgentSkillsListKeyParams = ReturnType<typeof toWorkspaceAgentSkillsListApiParams> & {
  tab: UseWorkspaceAgentSkillsListParams['tab'];
};

export type WorkspaceAgentSkillsListKey = [typeof WORKSPACE_AGENT_SKILLS_LIST_KEY, WorkspaceAgentSkillsListKeyParams];

export const getWorkspaceAgentSkillsListKey = (
  params: UseWorkspaceAgentSkillsListParams,
): WorkspaceAgentSkillsListKey => [
  WORKSPACE_AGENT_SKILLS_LIST_KEY,
  { ...toWorkspaceAgentSkillsListApiParams(params), tab: params.tab },
];

export const isWorkspaceAgentSkillsListKey = (key: QueryKey): key is WorkspaceAgentSkillsListKey =>
  Array.isArray(key) && key[0] === WORKSPACE_AGENT_SKILLS_LIST_KEY && isObject(key[1]);

export type UseWorkspaceAgentSkillsListOptions = {
  enabled?: boolean;
};

export const useWorkspaceAgentSkillsList = (
  params: UseWorkspaceAgentSkillsListParams,
  options?: UseWorkspaceAgentSkillsListOptions,
) => {
  const { workspaceId } = params;
  const listParams = toWorkspaceAgentSkillsListApiParams(params);

  const queryFn = !workspaceId
    ? skipToken
    : async ({ pageParam }: { pageParam: string }) => {
        const { workspaceId: _workspaceId, ...apiParams } = listParams;

        return callAirApi(() =>
          Skills.list({
            ...apiParams,
            workspaceId,
            cursor: pageParam || undefined,
          }),
        );
      };

  return useInfiniteQuery({
    queryKey: getWorkspaceAgentSkillsListKey(params),
    queryFn,
    getNextPageParam: (lastPage) => (lastPage.pagination?.hasMore ? (lastPage.pagination.cursor ?? null) : null),
    initialPageParam: '',
    enabled: Boolean(workspaceId) && options?.enabled,
    staleTime: FIVE_MINUTES,
  });
};
